import { CheckoutUpgradeSubscription } from '@/components/CheckoutSubscriptionPlan'
import { Loader } from '@/components/custom-components/Loader'
import OnboardingHeader from '@/components/onboarding/OnboardingHeader.tsx'
import { ProtectedArea } from '@/components/protectedArea'
import { Button } from '@/components/shadcn/button'
import { Dialog, DialogContent } from '@/components/shadcn/dialog'
import { useDowngradeUserPlan } from '@/hooks/useDowngradeUserPlan'
import useUserPermissions from '@/hooks/useUserPermissions'
import { FREE_PLAN_ID, PRO_PLAN, SPARK_PLAN, STANDARD_PLAN, plans } from '@/utils/data/plans'
import { CheckCircle } from '@mynaui/icons-react'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

type SearchParams = {
  plan?: string
}

export const Route = createFileRoute('/reactivation')({
  component: Reactivation,
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    const plan = search?.plan as string | undefined

    return {
      plan,
    }
  },
})

function Reactivation() {
  const { plan } = Route.useSearch()

  const { activateToFree, activateToFreeIsPending, isActivationSuccess } = useDowngradeUserPlan()

  const [activateOpen, setActivateOpen] = useState<boolean>(false)

  const { permissions, isLoading: isLoadingPermissions } = useUserPermissions()

  const userHasPlan = !!permissions?.usersPlans.length && !isLoadingPermissions
  const userHasNoPlan = !userHasPlan && !isLoadingPermissions

  const upgradeablePlans = [SPARK_PLAN.no_trial_plan_id, STANDARD_PLAN.no_trial_plan_id, PRO_PLAN.no_trial_plan_id]

  const planOnUpgradeablePlan = plan ? upgradeablePlans.includes(plan) : false

  const newPlan = plan && planOnUpgradeablePlan ? plans.find((item) => item.no_trial_plan_id === plan) : null

  const subscribeForFree = () => {
    if (userHasNoPlan && !(plan && planOnUpgradeablePlan)) {
      activateToFree({
        plans: [FREE_PLAN_ID],
        coupons: undefined,
      })
    }

    if (userHasPlan) {
      toast.info('Your account is already on a plan')
    }
  }

  const [isButtonDisabled, setIsButtonDisabled] = useState(true)

  useEffect(() => {
    if (isActivationSuccess) {
      setIsButtonDisabled(true)
      const timer = setTimeout(() => {
        setIsButtonDisabled(false)
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [isActivationSuccess])

  const handleNavigateToDashboard = () => {
    if (planOnUpgradeablePlan) {
      window.location.href = '/feeds/templates'
    } else {
      window.location.href = '/feeds/inspiration'
    }
  }

  useEffect(() => {
    subscribeForFree()
  }, [userHasNoPlan, userHasPlan])

  return (
    <ProtectedArea>
      <div className={'bg-brandgrad bg-no-repeat bg-center bg-cover min-h-screen'}>
        <div>
          <OnboardingHeader />
        </div>
        <div className={'flex-1 flex flex-col justify-center items-center py-[7.75rem]'}>
          <div className={'rounded-lg bg-white p-5 w-11/12 lg:w-[32rem] flex flex-col gap-6 border'}>
            <div className={''}>
              <h4 className={'text-2xl text-center text-themeforeground font-semibold'}>Account Reactivation</h4>
            </div>
            <div>
              {activateToFreeIsPending ? (
                <div className="flex justify-center items-center w-full py-6 h-14">
                  <Loader />
                </div>
              ) : isActivationSuccess ? (
                <div className="text-center">
                  <h3 className="font-sembold text-lg">Welcome back!</h3>
                  <p>We have reactivated your account to a {plan ? newPlan?.name : 'Free Plan'}.</p>
                  <div className={'mt-6'}>
                    <Button
                      onClick={handleNavigateToDashboard}
                      type="button"
                      className="w-full"
                      disabled={isButtonDisabled}
                    >
                      {'Start Creating'}
                    </Button>
                  </div>
                </div>
              ) : planOnUpgradeablePlan ? (
                <div className="space-y-3">
                  <div>
                    <h4 className={'font-medium text-2xl'}>{newPlan?.name}</h4>
                    <p className={'mt-1'}>
                      <span className={'text-2xl font-semibold'}>{newPlan?.price}</span> / month, billed monthly
                    </p>
                    <p className={'text-sm font-light text-thememutedforeground'}>{newPlan?.description}</p>
                  </div>
                  <div>
                    <ul className={'space-y-3 mt-2'}>
                      {STANDARD_PLAN.features.map((feature, index) => (
                        <li key={index} className="flex gap-1 justify-start items-center text-thememutedforeground">
                          <CheckCircle className="w-4 h-4" />
                          <span className="text-sm font-normal leading-5 ">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="space-y-2">
                    <Button className="w-full" onClick={() => setActivateOpen(true)}>
                      Activate Plan
                    </Button>
                    <p className="text-sm text-thememutedforeground text-center">or</p>
                    <div className="flex justify-center">
                      <span
                        onClick={() =>
                          activateToFree({
                            plans: [FREE_PLAN_ID],
                            coupons: undefined,
                          })
                        }
                        className={'text-sm text-center underline cursor-pointer'}
                      >
                        Claim Your Free Account
                      </span>
                    </div>
                  </div>
                  <div>
                    {newPlan ? (
                      <Dialog open={activateOpen} onOpenChange={() => setActivateOpen(false)}>
                        <DialogContent
                          className={`bg-themebackground dark:bg-themebackgrounddark sm:max-w-lg overflow-y-auto max-h-screen`}
                          onInteractOutside={(e) => e.preventDefault()}
                          onEscapeKeyDown={(e) => e.preventDefault()}
                        >
                          <CheckoutUpgradeSubscription
                            userHasPlan={userHasPlan}
                            selectedPlan={newPlan}
                            source={'campaign'}
                          />
                        </DialogContent>
                      </Dialog>
                    ) : null}
                  </div>
                </div>
              ) : (
                !userHasNoPlan &&
                !isLoadingPermissions && (
                  <div className="text-center">
                    <h3 className="font-sembold text-lg">You are already on a plan!</h3>
                    <div className={'mt-6'}>
                      <Button
                        onClick={() => {
                          window.location.href = '/feeds/templates'
                        }}
                        type={'button'}
                        className="w-full"
                      >
                        {'Start Creating'}
                      </Button>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </ProtectedArea>
  )
}
